/**
 * LogViewer Component
 *
 * Embedded log viewer with filters for Environment, Artifact, and Level.
 * Features real-time log streaming via WebSocket and "Open in Grafana" link.
 */

import { useRef, useEffect, useState, type CSSProperties } from 'react';
import { tokens } from './tokens';
import {
  LogLevelSchema,
  type BrutalLogEntry,
  type LogLevel,
  type LogFilters,
} from '../hooks/useLogEvents';
import { buildGrafanaUrlFromFilters } from '../lib/grafana';

// ═══════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════

export interface LogViewerProps {
  logs: BrutalLogEntry[];
  autoScroll: boolean;
  maxLines: number;
  filters: LogFilters;
  onFiltersChange: (filters: LogFilters) => void;
  availableEnvironments: string[];
  availableArtifacts: string[];
  grafanaUrl: string | null;
  style: CSSProperties | null;
}

// ═══════════════════════════════════════════════════════════════
// STYLES
// ═══════════════════════════════════════════════════════════════

const containerStyle: CSSProperties = {
  backgroundColor: tokens.colors.void,
  border: `1px solid ${tokens.colors.border}`,
  borderRadius: tokens.radii.lg,
  fontFamily: tokens.fonts.mono,
  fontSize: tokens.fontSizes.xs,
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
};

const toolbarStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: tokens.spacing[3],
  padding: tokens.spacing[3],
  borderBottom: `1px solid ${tokens.colors.border}`,
  backgroundColor: tokens.colors.surface,
  flexWrap: 'wrap',
};

const filterGroupStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: tokens.spacing[2],
};

const labelStyle: CSSProperties = {
  color: tokens.colors.textMuted,
  fontSize: tokens.fontSizes.xs,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
};

const selectStyle: CSSProperties = {
  backgroundColor: tokens.colors.void,
  border: `1px solid ${tokens.colors.border}`,
  borderRadius: tokens.radii.sm,
  color: tokens.colors.textPrimary,
  fontFamily: tokens.fonts.mono,
  fontSize: tokens.fontSizes.xs,
  padding: `${tokens.spacing[1]} ${tokens.spacing[2]}`,
  minWidth: '120px',
  cursor: 'pointer',
  outline: 'none',
};

const scrollAreaStyle: CSSProperties = {
  flex: 1,
  overflowY: 'auto',
  padding: tokens.spacing[3],
};

const logLineStyle = (level: LogLevel): CSSProperties => {
  const colors: Record<LogLevel, string> = {
    debug: tokens.colors.textMuted,
    info: tokens.colors.textPrimary,
    warn: tokens.colors.warning,
    error: tokens.colors.danger,
  };
  return {
    display: 'flex',
    alignItems: 'flex-start',
    gap: tokens.spacing[2],
    marginBottom: tokens.spacing[1],
    color: colors[level],
    lineHeight: '1.4',
  };
};

const timestampStyle: CSSProperties = {
  color: tokens.colors.textMuted,
  flexShrink: 0,
  minWidth: '80px',
};

const levelBadgeStyle = (level: LogLevel): CSSProperties => {
  const bgColors: Record<LogLevel, string> = {
    debug: 'rgba(136, 136, 136, 0.2)',
    info: 'rgba(0, 255, 135, 0.2)',
    warn: 'rgba(255, 165, 2, 0.2)',
    error: 'rgba(255, 71, 87, 0.2)',
  };
  const textColors: Record<LogLevel, string> = {
    debug: tokens.colors.textMuted,
    info: tokens.colors.mint,
    warn: tokens.colors.warning,
    error: tokens.colors.danger,
  };
  return {
    backgroundColor: bgColors[level],
    color: textColors[level],
    padding: `0 ${tokens.spacing[1]}`,
    borderRadius: tokens.radii.sm,
    fontSize: tokens.fontSizes.xs,
    fontWeight: tokens.fontWeights.medium,
    flexShrink: 0,
    minWidth: '48px',
    textAlign: 'center',
  };
};

const contextStyle: CSSProperties = {
  color: tokens.colors.mint,
  flexShrink: 0,
  opacity: 0.7,
};

const messageStyle: CSSProperties = {
  flex: 1,
  wordBreak: 'break-word',
};

const buttonStyle: CSSProperties = {
  backgroundColor: 'transparent',
  border: `1px solid ${tokens.colors.border}`,
  borderRadius: tokens.radii.sm,
  color: tokens.colors.textMuted,
  fontFamily: tokens.fonts.mono,
  fontSize: tokens.fontSizes.xs,
  padding: `${tokens.spacing[1]} ${tokens.spacing[2]}`,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  gap: tokens.spacing[1],
  textDecoration: 'none',
};

const buttonHoverStyle: CSSProperties = {
  ...buttonStyle,
  borderColor: tokens.colors.mint,
  color: tokens.colors.mint,
};

const emptyStateStyle: CSSProperties = {
  color: tokens.colors.textMuted,
  textAlign: 'center',
  padding: tokens.spacing[6],
};

// ═══════════════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════════════

/**
 * Format timestamp for display.
 */
function formatTimestamp(timestamp: string): string {
  try {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', {
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  } catch {
    return timestamp;
  }
}

/**
 * Build context string from log entry.
 */
function buildContext(log: BrutalLogEntry): string | null {
  const parts: string[] = [];
  if (log.environment !== null) {
    parts.push(log.environment);
  }
  if (log.artifact !== null) {
    parts.push(log.artifact);
  }
  if (log.host !== null) {
    parts.push(log.host);
  }
  return parts.length > 0 ? `[${parts.join('/')}]` : null;
}

// ═══════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════

export function LogViewer({
  logs,
  autoScroll,
  maxLines,
  filters,
  onFiltersChange,
  availableEnvironments,
  availableArtifacts,
  grafanaUrl,
  style,
}: LogViewerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isGrafanaHovered, setIsGrafanaHovered] = useState(false);

  // Auto-scroll effect
  useEffect(() => {
    if (autoScroll && containerRef.current !== null) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [logs, autoScroll]);

  // Limit displayed logs
  const displayLogs = logs.slice(-maxLines);

  // Level options
  const levelOptions: Array<LogLevel | null> = [null, 'debug', 'info', 'warn', 'error'];

  const handleEnvironmentChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value === '' ? null : e.target.value;
    onFiltersChange({ ...filters, environment: value });
  };

  const handleArtifactChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value === '' ? null : e.target.value;
    onFiltersChange({ ...filters, artifact: value });
  };

  const handleLevelChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    if (e.target.value === '') {
      onFiltersChange({ ...filters, level: null });
      return;
    }
    const parsed = LogLevelSchema.safeParse(e.target.value);
    if (parsed.success) {
      onFiltersChange({ ...filters, level: parsed.data });
    }
  };

  return (
    <div style={{ ...containerStyle, ...(style ?? {}) }}>
      {/* Toolbar */}
      <div style={toolbarStyle}>
        {/* Environment Filter */}
        <div style={filterGroupStyle}>
          <span style={labelStyle}>Env</span>
          <select
            style={selectStyle}
            value={filters.environment ?? ''}
            onChange={handleEnvironmentChange}
            aria-label="Filter logs by environment"
          >
            <option value="">All</option>
            {availableEnvironments.map((env) => (
              <option key={env} value={env}>
                {env}
              </option>
            ))}
          </select>
        </div>

        {/* Artifact Filter */}
        <div style={filterGroupStyle}>
          <span style={labelStyle}>Artifact</span>
          <select
            style={selectStyle}
            value={filters.artifact ?? ''}
            onChange={handleArtifactChange}
            aria-label="Filter logs by artifact"
          >
            <option value="">All</option>
            {availableArtifacts.map((art) => (
              <option key={art} value={art}>
                {art}
              </option>
            ))}
          </select>
        </div>

        {/* Level Filter */}
        <div style={filterGroupStyle}>
          <span style={labelStyle}>Level</span>
          <select
            style={selectStyle}
            value={filters.level ?? ''}
            onChange={handleLevelChange}
            aria-label="Filter logs by severity level"
          >
            {levelOptions.map((level) => (
              <option key={level ?? 'all'} value={level ?? ''}>
                {level === null ? 'All' : level.toUpperCase()}
              </option>
            ))}
          </select>
        </div>

        {/* Spacer */}
        <div style={{ flex: 1 }} />

        {/* Open in Grafana */}
        {grafanaUrl !== null && (
          <a
            href={buildGrafanaUrlFromFilters(
              {
                environment: filters.environment,
                artifact: filters.artifact,
                level: filters.level,
                deploymentId: null,
                host: null,
              },
              { baseUrl: grafanaUrl },
            )}
            target="_blank"
            rel="noopener noreferrer"
            style={isGrafanaHovered ? buttonHoverStyle : buttonStyle}
            onMouseEnter={() => setIsGrafanaHovered(true)}
            onMouseLeave={() => setIsGrafanaHovered(false)}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
              <polyline points="15 3 21 3 21 9" />
              <line x1="10" y1="14" x2="21" y2="3" />
            </svg>
            Open in Grafana
          </a>
        )}
      </div>

      {/* Log Content */}
      <div ref={containerRef} style={scrollAreaStyle}>
        {displayLogs.length === 0 ? (
          <div style={emptyStateStyle}>No logs match the current filters</div>
        ) : (
          displayLogs.map((log, i) => {
            const context = buildContext(log);
            return (
              <div key={`${log.timestamp}-${i}`} style={logLineStyle(log.level)}>
                <span style={timestampStyle}>{formatTimestamp(log.timestamp)}</span>
                <span style={levelBadgeStyle(log.level)}>{log.level.toUpperCase()}</span>
                {context !== null && <span style={contextStyle}>{context}</span>}
                <span style={messageStyle}>{log.message}</span>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
